import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

// Sparkle component for cursor trail
const Sparkle = ({ position, onComplete }) => {
   return (
      <motion.div
         className="pointer-events-none absolute w-3 h-3 z-50"
         style={{ left: position.x, top: position.y }}
         initial={{ scale: 1, opacity: 0.8 }}
         animate={{
            scale: 0,
            opacity: 0,
            transition: { duration: 0.35, ease: "easeOut" }
         }}
         onAnimationComplete={onComplete}
      >
         <div className="w-full h-full bg-gradient-to-br from-green-300 via-emerald-400 to-teal-300 rounded-full blur-[1px] shadow-lg shadow-green-400/50" />
      </motion.div>
   );
};

// Custom hook for managing sparkles
export const useTrailEffect = (id) => {
   const [sparkles, setSparkles] = useState([]);
   const [isHovering, setIsHovering] = useState(false);
   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
   const [lastSpawnTime, setLastSpawnTime] = useState(0);

   useEffect(() => {
      if (!isHovering) {
         setSparkles([]); // Clear sparkles when not hovering
         return;
      }

      const spawnSparkles = () => {
         if (mousePosition.x === 0 && mousePosition.y === 0) return;

         const now = Date.now();
         // Only spawn new sparkles if enough time has passed (5ms)
         if (now - lastSpawnTime < 5) return;

         setLastSpawnTime(now);

         // Create 3 sparkles at once for a denser trail
         const newSparkles = Array.from({ length: 3 }, (_, i) => ({
            id: `${id}-${now}-${i}`,
            position: {
               x: mousePosition.x - 6 + (Math.random() - 0.5) * 8,
               y: mousePosition.y - 6 + (Math.random() - 0.5) * 8,
            }
         }));

         setSparkles(prevSparkles => {
            const filteredSparkles = prevSparkles.slice(-35); // Keep more sparkles for denser trail
            return [...filteredSparkles, ...newSparkles];
         });
      };

      // Use requestAnimationFrame for smoother animation
      let animationFrame;
      const animate = () => {
         spawnSparkles();
         animationFrame = requestAnimationFrame(animate);
      };
      animate();

      return () => {
         if (animationFrame) {
            cancelAnimationFrame(animationFrame);
         }
      };
   }, [isHovering, mousePosition, id, lastSpawnTime]);

   const removeSparkle = (sparkleId) => {
      setSparkles(prevSparkles => prevSparkles.filter(s => s.id !== sparkleId));
   };

   const handleMouseMove = (e) => {
      if (!isHovering) return;

      const rect = e.currentTarget.getBoundingClientRect();
      setMousePosition({
         x: e.clientX - rect.left,
         y: e.clientY - rect.top
      });
   };

   const handleMouseEnter = () => {
      setIsHovering(true);
      setSparkles([]); // Clear any existing sparkles
   };

   const handleMouseLeave = () => {
      setIsHovering(false);
      setSparkles([]); // Clear sparkles when mouse leaves
   };

   return {
      sparkles,
      handleMouseEnter,
      handleMouseLeave,
      handleMouseMove,
      removeSparkle
   };
};

// SparkleTrail component
const SparkleTrail = ({ sparkles, onComplete }) => {
   return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
         {sparkles.map(sparkle => (
            <Sparkle
               key={sparkle.id}
               position={sparkle.position}
               onComplete={() => onComplete(sparkle.id)}
            />
         ))}
      </div>
   );
};

export default SparkleTrail;